/**
 * Tool definitions for Edge AI.
 * Descriptions are kept minimal — the AI uses get_tools to load full schemas on demand.
 */

import type { ToolDefinition } from '../types/api'
import type { ToolTags } from './tool-manifest'

// ── Meta Tool (always included) ────────────────────────────

export const GET_TOOLS_TOOL: ToolDefinition = {
  name: 'get_tools',
  description:
    'Load the input schemas of tools named in the "additional tools" list, before using one of them for the first time. Tools you already have in full (search_knowledge, get_platform_guide, ask_analyst, read_panel_state and the others in your tool list) need no loading: call them directly.',
  input_schema: {
    type: 'object',
    properties: {
      tools: {
        type: 'array',
        items: { type: 'string' },
        description: 'Tool names to load (e.g. ["set_filters", "read_panel_state"])',
      },
    },
    required: ['tools'],
  },
}

export const REQUEST_PANEL_SELECTION_TOOL: ToolDefinition = {
  name: 'request_panel_selection',
  description: 'Ask the user to click a panel when multiple match. Highlights matching panels.',
  input_schema: {
    type: 'object',
    properties: {
      panel_type: {
        type: 'string',
        description: 'Only highlight panels of this type (e.g. "scanner"). Omit for all.',
      },
      message: { type: 'string', description: 'Question to show (e.g. "Which scanner?")' },
    },
    required: ['message'],
  },
}

// ── Dashboard Panel Tools ──────────────────────────────────

export const ADD_PANEL_TOOL: ToolDefinition = {
  name: 'add_panel',
  description: 'Add a panel to the workspace.',
  input_schema: {
    type: 'object',
    properties: {
      component_type: {
        type: 'string',
        enum: [
          'chart',
          'scanner',
          'multi-scanner',
          'watchlist',
          'news',
          'key-stats',
          'ticker-stats',
          'bar-chart',
          'calendar',
          'notebook',
        ],
      },
      initial_state: { type: 'object', additionalProperties: true },
    },
    required: ['component_type'],
  },
}

export const DELETE_PANEL_TOOL: ToolDefinition = {
  name: 'delete_panel',
  description: 'Remove a panel.',
  input_schema: {
    type: 'object',
    properties: {
      panel_id: { type: 'string' },
    },
    required: ['panel_id'],
  },
}

export const SET_TICKER_TOOL: ToolDefinition = {
  name: 'set_ticker',
  description: 'Change the active ticker. Linked panels update automatically.',
  input_schema: {
    type: 'object',
    properties: {
      ticker: { type: 'string' },
      panel_id: { type: 'string' },
    },
    required: ['ticker'],
  },
}

export const SET_FOCUS_DATE_TOOL: ToolDefinition = {
  name: 'set_focus_date',
  description: 'Set historical date (YYYY-MM-DD) or null for live.',
  input_schema: {
    type: 'object',
    properties: {
      date: { type: ['string', 'null'] },
    },
    required: ['date'],
  },
}

export const SET_LINK_MODE_TOOL: ToolDefinition = {
  name: 'set_link_mode',
  description: 'Set panel link mode.',
  input_schema: {
    type: 'object',
    properties: {
      panel_id: { type: 'string' },
      mode: { type: 'string', enum: ['onChange', 'none', 'blue', 'green', 'yellow', 'purple'] },
    },
    required: ['panel_id', 'mode'],
  },
}

export const SET_NEWS_SOURCES_TOOL: ToolDefinition = {
  name: 'set_news_sources',
  description: 'Set active news sources ("news" and/or "sec").',
  input_schema: {
    type: 'object',
    properties: {
      sources: { type: 'array', items: { type: 'string', enum: ['news', 'sec'] } },
      panel_id: { type: 'string' },
    },
    required: ['sources'],
  },
}

// ── Filter Tools ───────────────────────────────────────────

export const SET_FILTERS_TOOL: ToolDefinition = {
  name: 'set_filters',
  description:
    "Set filters on a panel, or on a page's own filters (the History page's page-wide filters, the Playbook's play filters) when no panel with filters is open. For each column you send, any existing filter on that column is replaced. Filters on columns you do NOT send are preserved. Use the \"between\" operator for ranges. Adds and updates in one tool: it never duplicates a column.",
  input_schema: {
    type: 'object',
    properties: {
      panel_id: {
        type: 'string',
        description:
          "The panel to filter. Pass it when more than one panel with filters is open; omit it for the panel the user last clicked, or the only one. On the History page or the Playbook, omit it for the page's own filters.",
      },
      filters: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            left: {
              type: 'object',
              properties: {
                type: { type: 'string', enum: ['column'] },
                column: { type: 'string' },
              },
              required: ['type', 'column'],
            },
            operator: {
              type: 'string',
              enum: [
                'gt',
                'gte',
                'lt',
                'lte',
                'eq',
                'neq',
                'between',
                'contains',
                'not_contains',
                'in',
                'not_in',
                'is_true',
                'is_false',
              ],
            },
            right: {
              type: 'object',
              properties: {
                type: { type: 'string', enum: ['value', 'column'] },
                value: {},
                column: { type: 'string' },
              },
              required: ['type'],
            },
          },
          required: ['left', 'operator', 'right'],
        },
      },
    },
    required: ['filters'],
  },
}

export const SET_HISTORY_FILTERS_TOOL: ToolDefinition = {
  name: 'set_history_filters',
  description:
    'Set the page-wide History filters (every history panel reacts). Flat params: [{ left: { type: "column", column }, operator, right: { type: "value", value } }]. Replaces any existing filter on each column you send; filters on other columns are preserved. Use "between" for ranges. Distinct from the panel-level set_filters — this drives the shared page filter, not one panel.',
  input_schema: {
    type: 'object',
    properties: {
      filters: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            left: {
              type: 'object',
              properties: {
                type: { type: 'string', enum: ['column'] },
                column: { type: 'string' },
              },
              required: ['type', 'column'],
            },
            operator: {
              type: 'string',
              enum: [
                'gt',
                'gte',
                'lt',
                'lte',
                'eq',
                'neq',
                'between',
                'contains',
                'not_contains',
                'in',
                'not_in',
                'is_true',
                'is_false',
              ],
            },
            right: {
              type: 'object',
              properties: {
                type: { type: 'string', enum: ['value', 'column'] },
                value: {},
                column: { type: 'string' },
              },
              required: ['type'],
            },
          },
          required: ['left', 'operator', 'right'],
        },
      },
    },
    required: ['filters'],
  },
}

export const CLEAR_FILTERS_TOOL: ToolDefinition = {
  name: 'clear_filters',
  description: 'Remove all filters from a panel.',
  input_schema: {
    type: 'object',
    properties: {
      panel_id: {
        type: 'string',
        description:
          'The panel to clear. Pass it when more than one panel with filters is open; omit it for the panel the user last clicked, or the only one.',
      },
    },
    required: [],
  },
}

// ── Column Tools ───────────────────────────────────────────

export const ADD_COLUMNS_TOOL: ToolDefinition = {
  name: 'add_columns',
  description: 'Append columns. Existing preserved.',
  input_schema: {
    type: 'object',
    properties: {
      panel_id: {
        type: 'string',
        description:
          'The panel to change. Pass it when more than one panel with columns is open; omit it for the panel the user last clicked, or the only one.',
      },
      columns: { type: 'array', items: { type: 'string' } },
    },
    required: ['columns'],
  },
}

export const REMOVE_COLUMNS_TOOL: ToolDefinition = {
  name: 'remove_columns',
  description: 'Remove specific columns. Ticker cannot be removed.',
  input_schema: {
    type: 'object',
    properties: {
      panel_id: {
        type: 'string',
        description:
          'The panel to change. Pass it when more than one panel with columns is open; omit it for the panel the user last clicked, or the only one.',
      },
      columns: { type: 'array', items: { type: 'string' } },
    },
    required: ['columns'],
  },
}

export const SET_COLUMNS_TOOL: ToolDefinition = {
  name: 'set_columns',
  description: 'Replace ALL columns. Ticker auto-included.',
  input_schema: {
    type: 'object',
    properties: {
      panel_id: {
        type: 'string',
        description:
          'The panel to change. Pass it when more than one panel with columns is open; omit it for the panel the user last clicked, or the only one.',
      },
      columns: { type: 'array', items: { type: 'string' } },
    },
    required: ['columns'],
  },
}

// ── Read Tools ─────────────────────────────────────────────

/**
 * The model asks to SEE the screen (2026-10-02). Screenshots used to be taken
 * up front from a keyword guess, costing seconds on most voice turns; now the
 * model decides. Executed by the manager itself (capture + annotate), and the
 * image is attached to the next round.
 */
export const VIEW_SCREEN_TOOL: ToolDefinition = {
  name: 'view_screen',
  description:
    "Look at the user's screen. Use ONLY for things that exist visually and nowhere in your data: an error or warning message on screen, a dialog or unusual UI state the user describes, or the user's own chart drawings. NOT for a chart's shape or price action (get the bars with ask_analyst for the chart's ticker and timeframe from read_panel_state), NOT for the highlighted or selected row (read_panel_state and @-mentioned panel data already include it), NOT for panel contents or market data. Takes a few seconds; the screenshot arrives with the next message.",
  input_schema: { type: 'object', properties: {}, required: [] },
}

export const READ_PANEL_STATE_TOOL: ToolDefinition = {
  name: 'read_panel_state',
  description: 'Read current panel state (filters, columns, config). Read-only.',
  input_schema: {
    type: 'object',
    properties: {
      panel_id: { type: 'string' },
      panel_type: { type: 'string' },
    },
    required: [],
  },
}

export const GET_PLATFORM_GUIDE_TOOL: ToolDefinition = {
  name: 'get_platform_guide',
  description:
    'Look up how to do something in the Edge platform — where a control lives, how to configure a feature, the exact click-path for a task, or what a column/metric means. Call this BEFORE answering any "how do I…", "where is…", or "what does … mean" question about the app UI, so you give the real steps instead of guessing. Returns concise task recipes; if it returns nothing useful, say so rather than inventing UI paths. Some recipes have an interactive on-screen walkthrough that points the user through the steps; it starts only when `wants_to_do_it` is true (otherwise the user gets a Start walkthrough button). Pass `panel` so it points at the right panel.',
  input_schema: {
    type: 'object',
    properties: {
      topic: {
        type: 'string',
        description:
          "The user's question or task in natural language, e.g. 'how do I color-code a column' or 'where is the scanner date picker'.",
      },
      panel: {
        type: 'string',
        description:
          "Which panel the user's request is about, e.g. 'scanner', 'multi-scanner', 'history', 'watchlist', 'news', 'calendar', 'ticker-stats'. Infer it from what the user said ('the scan' → scanner) or the panel they're looking at. Needed so an on-screen walkthrough points at the correct panel; omit only if the request is not about a specific panel.",
      },
      wants_to_do_it: {
        type: 'boolean',
        description:
          'true when the user wants to do the task now ("how do I…", "show me…", "set it up"); false when they ask whether something is possible, how it works, or what it means.',
      },
    },
    required: ['topic'],
  },
}

export const SEARCH_KNOWLEDGE_TOOL: ToolDefinition = {
  name: 'search_knowledge',
  description:
    'Search the knowledge base: trading terms and concepts (what is a PIPE, days to cover, profit factor, RSI: domain concept), columns, scan and strategy recipes, backtest Templates (domain cookbook) and Playbook plays (domain play). For a request with several concepts ("low float momo with news"), pass them all at once in `queries` (["low float", "momo", "news"]): they are searched in parallel and answered per concept. Trader shorthand (FRD, momo, MDR, SC, ETHG) resolves exactly: its scan setup and columns come first, with the meaning. Always search in English even if the user speaks another language.',
  input_schema: {
    type: 'object',
    properties: {
      query: { type: 'string', description: 'One concept. Use queries for several.' },
      queries: {
        type: 'array',
        items: { type: 'string' },
        maxItems: 8,
        description: 'Several concepts searched in parallel, one short phrase each.',
      },
      domain: {
        type: 'string',
        enum: ['column', 'scan', 'strategy', 'setup', 'concept', 'cookbook', 'play'],
      },
      panel_type: { type: 'string' },
      limit: { type: 'number' },
    },
  },
}

export const ASK_ANALYST_TOOL: ToolDefinition = {
  name: 'ask_analyst',
  description:
    'Ask a market-research analyst a data question and get a grounded, cited answer. Use this whenever the user asks WHY a ticker moved on a date, HOW OFTEN something happens (e.g. "how many times did it close red"), WHAT usually happens after an event (e.g. earnings), for a company\'s financial status, or about SETUPS ACROSS MANY STOCKS: "other stocks like this", "stocks that opened around $1 and ran to $2 in premarket", "which small caps gapped 50% on low float and what happened next". Also use it when the user asks about their own watchlists: which lists they have, what is in them, and how those tickers are doing. The analyst screens every ticker\'s past trading days as well as one ticker\'s history, fetches real market data (price history, news, filings, earnings, fundamentals, historical stats) and returns Markdown prose with citations. Prefer this over answering data questions yourself, and never tell the user a cross-ticker question is out of reach.',
  input_schema: {
    type: 'object',
    properties: {
      question: {
        type: 'string',
        description: 'The full natural-language question to answer.',
      },
      ticker: {
        type: 'string',
        description: 'Optional ticker symbol the question is about, e.g. "NVDA".',
      },
      date: {
        type: 'string',
        description: 'Optional ISO date (YYYY-MM-DD) the question concerns.',
      },
    },
    required: ['question'],
  },
}

export const SUMMARIZE_HISTORY_TOOL: ToolDefinition = {
  name: 'summarize_history',
  description:
    'Summarize or find patterns in the History page\'s CURRENT filtered dataset (the whole population matching the active History filters). Use when the user asks to summarize, describe, or find patterns in "this data", "these results", "my current filter", or the visible history population, rather than about one specific ticker. The analyst runs SQL over the filtered set and returns grounded, cited Markdown prose. Optionally pass `focus` to steer it. Only available on the History page.',
  input_schema: {
    type: 'object',
    properties: {
      focus: {
        type: 'string',
        description:
          'Optional angle to steer the summary, e.g. "sector concentration" or "biggest movers". Omit for a general summary.',
      },
    },
    required: [],
  },
}

export const SET_LANGUAGE_TOOL: ToolDefinition = {
  name: 'set_language',
  description:
    'Change the language Edge AI responds in when the user asks (e.g. "switch to Spanish", "responde en español", "back to English"). Takes effect immediately for all future replies. Supported codes: en (English), es (Spanish).',
  input_schema: {
    type: 'object',
    properties: {
      language: {
        type: 'string',
        enum: ['en', 'es'],
        description: 'Language code: "en" = English, "es" = Spanish.',
      },
    },
    required: ['language'],
  },
}

// ── History Tools ──────────────────────────────────────────

export const GENERATE_HISTORY_LAYOUT_TOOL: ToolDefinition = {
  name: 'generate_history_layout',
  description: 'Generate a history analysis layout from a description.',
  input_schema: {
    type: 'object',
    properties: {
      prompt: { type: 'string' },
    },
    required: ['prompt'],
  },
}

// ── Backtest Read Tools ────────────────────────────────────

export const READ_CURRENT_GRAPH_TOOL: ToolDefinition = {
  name: 'read_current_graph',
  description: 'Read current strategy graph (nodes, params, connections). Read-only.',
  input_schema: { type: 'object', properties: {}, required: [] },
}

export const READ_BACKTEST_RESULTS_TOOL: ToolDefinition = {
  name: 'read_backtest_results',
  description: 'Read latest backtest results (P&L, drawdown, Sharpe, trades). Read-only.',
  input_schema: {
    type: 'object',
    properties: {
      include_trades: { type: 'boolean' },
    },
    required: [],
  },
}

// ── Backtest Mutation Tools ────────────────────────────────

export const ADD_NODE_TOOL: ToolDefinition = {
  name: 'add_node',
  description: 'Add a node to the strategy graph.',
  input_schema: {
    type: 'object',
    properties: {
      node_type: { type: 'string' },
      position: { type: 'object', properties: { x: { type: 'number' }, y: { type: 'number' } } },
      parameters: { type: 'object', additionalProperties: true },
    },
    required: ['node_type'],
  },
}

export const CONNECT_NODES_TOOL: ToolDefinition = {
  name: 'connect_nodes',
  description: 'Connect two nodes in the strategy graph.',
  input_schema: {
    type: 'object',
    properties: {
      source_node_id: { type: 'string' },
      target_node_id: { type: 'string' },
      source_handle: { type: 'string' },
      target_handle: { type: 'string' },
    },
    required: ['source_node_id', 'target_node_id'],
  },
}

export const SET_NODE_PARAMETERS_TOOL: ToolDefinition = {
  name: 'set_node_parameters',
  description: 'Update parameters on a strategy node.',
  input_schema: {
    type: 'object',
    properties: {
      node_id: { type: 'string' },
      parameters: { type: 'object', additionalProperties: true },
    },
    required: ['node_id', 'parameters'],
  },
}

export const REMOVE_NODE_TOOL: ToolDefinition = {
  name: 'remove_node',
  description: 'Remove a node and its edges from the strategy graph.',
  input_schema: {
    type: 'object',
    properties: { node_id: { type: 'string' } },
    required: ['node_id'],
  },
}

export const CREATE_STRATEGY_TEMPLATE_TOOL: ToolDefinition = {
  name: 'create_strategy_template',
  description: 'Create a pre-built strategy template.',
  input_schema: {
    type: 'object',
    properties: {
      template_name: {
        type: 'string',
        enum: ['sma_crossover', 'rsi_reversal', 'breakout', 'mean_reversion'],
      },
      parameters: { type: 'object', additionalProperties: true },
      append: {
        type: 'boolean',
        description:
          'When true, ADD this to the existing graph instead of replacing it. Set true when the user asks to add/extend; omit (replace) when they ask to build/start a new strategy.',
      },
    },
    required: ['template_name'],
  },
}

export const CREATE_STRATEGY_FROM_RECIPE_TOOL: ToolDefinition = {
  name: 'create_strategy_from_recipe',
  description: 'Build a strategy from a compact recipe. Prefer over add_node/connect_nodes.',
  input_schema: {
    type: 'object',
    properties: {
      recipe: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            id: { type: 'string' },
            type: { type: 'string' },
            params: { type: 'object', additionalProperties: true },
            inputs: {
              type: 'object',
              description:
                'targetHandle → "sourceId.outputHandle" ref, or a number/boolean literal for static inputs',
              additionalProperties: { type: ['string', 'number', 'boolean'] },
            },
          },
          required: ['id', 'type'],
        },
      },
      append: {
        type: 'boolean',
        description:
          'When true, ADD this strategy to the existing graph instead of replacing it. Set true when the user asks to add to / extend their current strategy; omit (replace) when they ask to build or start a new one.',
      },
    },
    required: ['recipe'],
  },
}

export const APPLY_GRAPH_OPS_TOOL: ToolDefinition = {
  name: 'apply_graph_ops',
  description:
    'Apply a batch of edit ops to the current strategy graph in one transactional call (all-or-nothing — nothing changes if any op fails). Prefer for any multi-step edit. See the op-language section in the node catalog for op shapes.',
  input_schema: {
    type: 'object',
    properties: {
      ops: {
        type: 'array',
        description: 'Edit ops applied in order; the whole batch commits or none does.',
        items: {
          type: 'object',
          properties: {
            op: {
              type: 'string',
              enum: [
                'add_node',
                'set_params',
                'connect',
                'disconnect',
                'remove_node',
                'insert_between',
                'replace_node',
              ],
            },
            id: {
              type: 'string',
              description: 'Step id of the target node (or the new node for add/insert/replace).',
            },
            type: {
              type: 'string',
              description: 'Node type — required for add_node, insert_between, replace_node.',
            },
            params: { type: 'object', additionalProperties: true },
            from: {
              type: 'string',
              description: 'Source ref "stepId.handle" — for connect and insert_between.',
            },
            to: {
              type: 'string',
              description: 'Target ref "stepId.handle" — for connect, disconnect, insert_between.',
            },
          },
          required: ['op'],
        },
      },
    },
    required: ['ops'],
  },
}

const recipeSchema = (
  CREATE_STRATEGY_FROM_RECIPE_TOOL.input_schema.properties as Record<string, unknown>
).recipe
const graphOpsSchema = (APPLY_GRAPH_OPS_TOOL.input_schema.properties as Record<string, unknown>).ops

export const CREATE_SAVED_STRATEGY_TOOL: ToolDefinition = {
  name: 'create_saved_strategy',
  description:
    'Create an owned saved node-graph strategy from a complete runnable recipe without opening the editor. Returns strategy identity and a Build link; does not run it.',
  input_schema: {
    type: 'object',
    properties: { name: { type: 'string', minLength: 1, maxLength: 120 }, recipe: recipeSchema },
    required: ['name', 'recipe'],
    additionalProperties: false,
  },
}

export const READ_SAVED_STRATEGY_TOOL: ToolDefinition = {
  name: 'read_saved_strategy',
  description:
    'Read an owned saved strategy graph by explicit strategy ID before editing or running.',
  input_schema: {
    type: 'object',
    properties: { strategyId: { type: 'string' } },
    required: ['strategyId'],
    additionalProperties: false,
  },
}

export const EDIT_SAVED_STRATEGY_TOOL: ToolDefinition = {
  name: 'edit_saved_strategy',
  description:
    'Atomically apply validated graph operations to an owned saved strategy. Supply its latest graphRevision from read_saved_strategy; conflicts require another read.',
  input_schema: {
    type: 'object',
    properties: {
      strategyId: { type: 'string' },
      expectedGraphRevision: { type: 'integer', minimum: 0 },
      ops: graphOpsSchema,
    },
    required: ['strategyId', 'expectedGraphRevision', 'ops'],
    additionalProperties: false,
  },
}

export const RUN_SAVED_STRATEGY_TOOL: ToolDefinition = {
  name: 'run_saved_strategy',
  description:
    'Run one ticker and target date on an owned saved strategy without opening the editor. Returns immediate metrics and an execution receipt; no batch or Report link.',
  input_schema: {
    type: 'object',
    properties: {
      strategyId: { type: 'string' },
      graphRevision: { type: 'integer', minimum: 0 },
      ticker: { type: 'string' },
      targetDate: { type: 'string', description: 'YYYY-MM-DD' },
      initialCash: { type: 'number', exclusiveMinimum: 0 },
    },
    required: ['strategyId', 'graphRevision', 'ticker', 'targetDate'],
    additionalProperties: false,
  },
}

export const RERUN_BACKTEST_TOOL: ToolDefinition = {
  name: 'rerun_backtest',
  description:
    'Re-run the backtest on the current strategy graph, reusing the last run inputs. Use after applying an improvement, or when the user asks to run/re-run. No parameters.',
  input_schema: { type: 'object', properties: {}, required: [] },
}

export const GET_NODE_SPECS_TOOL: ToolDefinition = {
  name: 'get_node_specs',
  description:
    'Get full parameter/input/output details for specific node types before building a recipe. Read-only.',
  input_schema: {
    type: 'object',
    properties: {
      types: {
        type: 'array',
        items: { type: 'string' },
        description: 'Node types from the catalog',
      },
    },
    required: ['types'],
  },
}

// ── Navigation Tool ────────────────────────────────────────

export const NAVIGATE_TO_TOOL: ToolDefinition = {
  name: 'navigate_to',
  description:
    'Navigate the app to a different page. Prefer doing this instead of telling the user where to click. Provide params for routes that require them (see the route catalog in the system prompt).',
  input_schema: {
    type: 'object',
    properties: {
      route_id: {
        type: 'string',
        description: 'The route id from the route catalog (e.g. "dashboard", "backtest-stats")',
      },
      params: {
        type: 'object',
        additionalProperties: { type: 'string' },
        description: 'Route params to fill in (e.g. { "id": "abc123" })',
      },
    },
    required: ['route_id'],
  },
}

// ── All tools ──────────────────────────────────────────────

export const ALL_TOOLS: ToolDefinition[] = [
  GET_TOOLS_TOOL,
  REQUEST_PANEL_SELECTION_TOOL,
  SEARCH_KNOWLEDGE_TOOL,
  ASK_ANALYST_TOOL,
  SUMMARIZE_HISTORY_TOOL,
  SET_LANGUAGE_TOOL,
  NAVIGATE_TO_TOOL,
  ADD_PANEL_TOOL,
  DELETE_PANEL_TOOL,
  SET_TICKER_TOOL,
  SET_FOCUS_DATE_TOOL,
  SET_LINK_MODE_TOOL,
  SET_NEWS_SOURCES_TOOL,
  SET_FILTERS_TOOL,
  SET_HISTORY_FILTERS_TOOL,
  CLEAR_FILTERS_TOOL,
  ADD_COLUMNS_TOOL,
  REMOVE_COLUMNS_TOOL,
  SET_COLUMNS_TOOL,
  READ_PANEL_STATE_TOOL,
  VIEW_SCREEN_TOOL,
  GET_PLATFORM_GUIDE_TOOL,
  GENERATE_HISTORY_LAYOUT_TOOL,
  READ_CURRENT_GRAPH_TOOL,
  READ_BACKTEST_RESULTS_TOOL,
  ADD_NODE_TOOL,
  CONNECT_NODES_TOOL,
  SET_NODE_PARAMETERS_TOOL,
  REMOVE_NODE_TOOL,
  CREATE_STRATEGY_TEMPLATE_TOOL,
  CREATE_STRATEGY_FROM_RECIPE_TOOL,
  APPLY_GRAPH_OPS_TOOL,
  CREATE_SAVED_STRATEGY_TOOL,
  READ_SAVED_STRATEGY_TOOL,
  EDIT_SAVED_STRATEGY_TOOL,
  RUN_SAVED_STRATEGY_TOOL,
  RERUN_BACKTEST_TOOL,
  GET_NODE_SPECS_TOOL,
]

/** Map of tool name → full definition for get_tools lookup */
export const TOOL_MAP: Record<string, ToolDefinition> = Object.fromEntries(
  ALL_TOOLS.map((t) => [t.name, t]),
)

// ── Manifests ──────────────────────────────────────────────

/**
 * Each static tool's manifest by name (spec 2026-10-18 AI companion growth
 * architecture, 4.1): the effect table, the always-ask set and the panel gates
 * are read from these. Kept apart from the definitions above, which are the
 * shape sent to the model on every turn.
 */
export const TOOL_TAGS: Record<string, ToolTags> = {
  // Loaded on every turn: the meta tool and the answer tools.
  get_tools: {
    domains: ['assistant'],
    panels: [],
    operations: ['read'],
    effect: 'read',
    dataOwner: 'app',
    core: true,
    summary: 'Load the schemas of tools in the additional tools list',
  },
  get_platform_guide: {
    domains: ['app_help'],
    panels: [],
    operations: ['explain'],
    effect: 'read',
    dataOwner: 'app',
    core: true,
    summary: 'How to do something in the app: where a control lives and the steps',
  },
  request_panel_selection: {
    domains: ['workspace'],
    panels: [],
    operations: ['read'],
    effect: 'read',
    dataOwner: 'trader',
    takes: ['panel'],
    core: true,
    summary: 'Ask the trader to click a panel when several match',
  },
  search_knowledge: {
    domains: ['knowledge'],
    panels: [],
    operations: ['explain'],
    effect: 'read',
    dataOwner: 'app',
    core: true,
    summary:
      'Search the knowledge base: trading terms, columns, scan and strategy recipes, backtest Templates and Playbook plays',
  },
  ask_analyst: {
    domains: ['research'],
    panels: [],
    operations: ['read', 'explain'],
    effect: 'read',
    dataOwner: 'market',
    takes: ['ticker'],
    core: true,
    summary: 'Ask the market analyst a data question and get a cited answer',
  },
  // A preference, not the workspace.
  set_language: {
    domains: ['assistant'],
    panels: [],
    operations: ['change'],
    effect: 'read',
    dataOwner: 'app',
    core: true,
    summary: 'Change the language the assistant answers in',
  },
  read_panel_state: {
    domains: ['workspace'],
    panels: [],
    operations: ['read'],
    effect: 'read',
    dataOwner: 'trader',
    takes: ['panel'],
    core: true,
    summary: "Read a panel's current state: filters, columns, config",
  },
  view_screen: {
    domains: ['workspace'],
    panels: [],
    operations: ['read'],
    effect: 'read',
    dataOwner: 'trader',
    core: true,
    summary: "Look at the trader's screen, for what only shows there",
  },
  summarize_history: {
    domains: ['history', 'research'],
    panels: [],
    operations: ['explain'],
    effect: 'read',
    dataOwner: 'market',
    summary: "Summarize the History page's current filtered population",
  },
  // Moves the view; nothing is saved.
  navigate_to: {
    domains: ['workspace'],
    panels: [],
    operations: ['navigate'],
    effect: 'view',
    dataOwner: 'app',
    summary: 'Navigate to a different page in the app',
  },
  // A view state the workspace does not save.
  set_focus_date: {
    domains: ['workspace'],
    panels: [],
    operations: ['change'],
    effect: 'view',
    dataOwner: 'trader',
    summary: 'Set historical date or reset to live',
  },
  add_panel: {
    domains: ['workspace'],
    panels: [],
    operations: ['add'],
    effect: 'undoable',
    dataOwner: 'trader',
    summary: 'Add a panel (chart, scanner, watchlist, news, etc.)',
  },
  // A removal: it asks even with "apply without asking" on.
  delete_panel: {
    domains: ['workspace'],
    panels: [],
    operations: ['remove'],
    effect: 'confirm',
    alwaysAsk: true,
    dataOwner: 'trader',
    takes: ['panel'],
    summary: 'Remove a panel',
  },
  set_ticker: {
    domains: ['workspace'],
    panels: [],
    operations: ['change'],
    effect: 'undoable',
    dataOwner: 'trader',
    takes: ['ticker', 'panel'],
    summary: 'Change active ticker on a panel',
  },
  set_link_mode: {
    domains: ['workspace'],
    panels: [],
    operations: ['change'],
    effect: 'undoable',
    dataOwner: 'trader',
    takes: ['panel'],
    summary: 'Set panel ticker link mode',
  },
  set_news_sources: {
    domains: ['news'],
    panels: ['news'],
    operations: ['change'],
    effect: 'undoable',
    dataOwner: 'trader',
    takes: ['panel'],
    summary: 'Toggle news/SEC filing sources',
  },
  // Shared capabilities (spec 4.4): each reaches the panels and pages that declare it.
  set_filters: {
    domains: ['workspace'],
    panels: [],
    capability: 'filters',
    operations: ['add', 'change'],
    effect: 'undoable',
    dataOwner: 'trader',
    takes: ['panel'],
    summary:
      'Set filters by column (replaces same-column, preserves others). Use for both adds and updates.',
  },
  // The old name for set_filters on History. It always sets the page's own
  // filters, so it names no capability: no panel can reach it, and only the
  // history scope lists it.
  set_history_filters: {
    domains: ['workspace', 'history'],
    panels: [],
    operations: ['add', 'change'],
    effect: 'undoable',
    dataOwner: 'trader',
    summary: 'Old name for set_filters on the History page: the page-wide filters',
  },
  clear_filters: {
    domains: ['workspace'],
    panels: [],
    capability: 'filters',
    operations: ['remove'],
    effect: 'confirm',
    alwaysAsk: true,
    dataOwner: 'trader',
    takes: ['panel'],
    summary: 'Remove all filters from a panel',
  },
  add_columns: {
    domains: ['workspace'],
    panels: [],
    capability: 'columns',
    operations: ['add'],
    effect: 'undoable',
    dataOwner: 'trader',
    takes: ['panel'],
    summary: 'Append columns to a panel',
  },
  remove_columns: {
    domains: ['workspace'],
    panels: [],
    capability: 'columns',
    operations: ['remove'],
    effect: 'undoable',
    dataOwner: 'trader',
    takes: ['panel'],
    summary: 'Remove columns from a panel',
  },
  set_columns: {
    domains: ['workspace'],
    panels: [],
    capability: 'columns',
    operations: ['change'],
    effect: 'undoable',
    dataOwner: 'trader',
    takes: ['panel'],
    summary: 'Replace all columns on a panel',
  },
  // Replaces the layout, and charges a credit of its own.
  generate_history_layout: {
    domains: ['history'],
    panels: [],
    operations: ['change'],
    effect: 'confirm',
    dataOwner: 'trader',
    summary: 'Generate analysis layout from description',
  },
  // Backtest editor. The app registers the schemas of graph_read, graph_call and
  // analyze_backtest; their manifests sit here, beside the scopes that list them.
  graph_read: {
    domains: ['backtest'],
    panels: [],
    operations: ['read'],
    effect: 'read',
    dataOwner: 'trader',
    summary:
      'Inspect the active graph, strategy, chart layout, and exact execution results; help lists method signatures',
  },
  // Edits are undoable; toolEffect reads its lifecycle and run methods as confirm.
  graph_call: {
    domains: ['backtest'],
    panels: [],
    operations: ['change', 'run'],
    effect: 'undoable',
    dataOwner: 'trader',
    summary:
      'Edit, organize, save/load/create strategies, configure auto-run, and run one backtest (no strategy deletion or batch execution)',
  },
  read_current_graph: {
    domains: ['backtest'],
    panels: [],
    operations: ['read'],
    effect: 'read',
    dataOwner: 'trader',
    summary: 'Read strategy graph (recipe form)',
  },
  read_backtest_results: {
    domains: ['backtest'],
    panels: [],
    operations: ['read'],
    effect: 'read',
    dataOwner: 'trader',
    summary: 'Read latest backtest results',
  },
  get_node_specs: {
    domains: ['backtest'],
    panels: [],
    operations: ['read'],
    effect: 'read',
    dataOwner: 'app',
    summary: 'Read parameter and connection details for node types',
  },
  analyze_backtest: {
    domains: ['backtest'],
    panels: [],
    operations: ['read'],
    effect: 'read',
    dataOwner: 'trader',
    summary: 'Analyze artifacts from the current owned execution',
  },
  // Graph edits: their own EditDiffCard (Keep / Undo, Cmd+Z).
  create_strategy_from_recipe: {
    domains: ['backtest'],
    panels: [],
    operations: ['add'],
    effect: 'undoable',
    dataOwner: 'trader',
    summary: 'Build strategy from recipe',
  },
  apply_graph_ops: {
    domains: ['backtest'],
    panels: [],
    operations: ['change'],
    effect: 'undoable',
    dataOwner: 'trader',
    summary: 'Batch-edit the current graph transactionally (preferred for multi-step edits)',
  },
  create_strategy_template: {
    domains: ['backtest'],
    panels: [],
    operations: ['add'],
    effect: 'undoable',
    dataOwner: 'trader',
    summary: 'Load a pre-built strategy',
  },
  add_node: {
    domains: ['backtest'],
    panels: [],
    operations: ['add'],
    effect: 'undoable',
    dataOwner: 'trader',
    summary: 'Add a node to strategy graph',
  },
  connect_nodes: {
    domains: ['backtest'],
    panels: [],
    operations: ['add'],
    effect: 'undoable',
    dataOwner: 'trader',
    summary: 'Connect two nodes',
  },
  set_node_parameters: {
    domains: ['backtest'],
    panels: [],
    operations: ['change'],
    effect: 'undoable',
    dataOwner: 'trader',
    summary: 'Update node parameters',
  },
  remove_node: {
    domains: ['backtest'],
    panels: [],
    operations: ['remove'],
    effect: 'undoable',
    dataOwner: 'trader',
    summary: 'Remove a node',
  },
  rerun_backtest: {
    domains: ['backtest'],
    panels: [],
    operations: ['run'],
    effect: 'confirm',
    dataOwner: 'trader',
    summary: 'Re-run the backtest on the current strategy (reuses the last run inputs)',
  },
  // Saved strategies. A save has no inverse here (the AI never deletes a
  // strategy), so they ask.
  read_saved_strategy: {
    domains: ['backtest'],
    panels: [],
    operations: ['read'],
    effect: 'read',
    dataOwner: 'trader',
    takes: ['strategy'],
    summary: 'Read an owned saved strategy graph by ID',
  },
  create_saved_strategy: {
    domains: ['backtest'],
    panels: [],
    operations: ['add'],
    effect: 'confirm',
    dataOwner: 'trader',
    summary: 'Create a saved node-graph strategy from a recipe',
  },
  edit_saved_strategy: {
    domains: ['backtest'],
    panels: [],
    operations: ['change'],
    effect: 'confirm',
    dataOwner: 'trader',
    takes: ['strategy'],
    summary: 'Apply validated graph operations to a saved strategy',
  },
  run_saved_strategy: {
    domains: ['backtest'],
    panels: [],
    operations: ['run'],
    effect: 'confirm',
    dataOwner: 'trader',
    takes: ['strategy', 'ticker'],
    summary: 'Run one ticker and date on a saved strategy',
  },
}
